import React, { useMemo } from 'react';
import Slider from 'react-slick';
import '../../styles/slick/slick-theme.css';
import '../../styles/slick/slick.css';
import css from './SearchPage.module.css';

import img1 from '../../assets/plumbing.png';
import img2 from '../../assets/lighting.png';
import img3 from '../../assets/furniture.png';
import img4 from '../../assets/textiles.png';
import img5 from '../../assets/artAndDecor.png';
import img6 from '../../assets/stones.webp';

import { formatCategoryLabel } from '../../util/genericHelper';
import { categoryLevel1 } from '../../util/constants';

// ONLY these categories should display (real images, no fallback)
const CATEGORY_CONFIG = [
  { slug: 'cat-stone-tile', image: img6 },
  { slug: 'cat-lighting', image: img2 },
  { slug: 'cat-furniture', image: img3 },
  { slug: 'cat-textiles', image: img4 },
  { slug: 'cat-art-decor', image: img5 },
  { slug: 'cat-plumbing', image: img1 }, // keep if you actually have this category
];

// Quick lookup for image by slug
const CATEGORY_IMAGE_MAP = CATEGORY_CONFIG.reduce((acc, item) => {
  acc[item.slug] = item.image;
  return acc;
}, {});

function SectionShopByCategory({ intl, useHistoryPush, listingCategories }) {
  const title = intl.formatMessage({ id: 'SearchPage.SectionShopByCategory.title' });
  const paramName = categoryLevel1;

  const categoryItems = useMemo(() => {
    const cats = Array.isArray(listingCategories) ? listingCategories : [];

    // Build lookup so we can fetch name/label from API categories
    const catById = cats.reduce((acc, cat) => {
      const id = String(cat?.id || '').trim();
      if (id) acc[id] = cat;
      return acc;
    }, {});

    // Only show categories defined in CATEGORY_CONFIG, and keep this exact order
    return CATEGORY_CONFIG
      .map(({ slug, image }) => {
        const cat = catById[slug];
        if (!cat) return null; // hide if not present in backend categories
        return {
          slug,
          image,
          label: formatCategoryLabel(cat?.name || slug),
        };
      })
      .filter(Boolean);
  }, [listingCategories]);

  const SliderSettings = {
    dots: false,
    arrows: true,
    infinite: false,
    autoplay: false,
    autoplaySpeed: 2000,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    variableWidth: false,
    focusOnSelect: false,
    centerMode: false,
    responsive: [
      { breakpoint: 1500, settings: { slidesToShow: 6 } },
      { breakpoint: 1300, settings: { slidesToShow: 5 } },
      { breakpoint: 1024, settings: { slidesToShow: 5 } },
      { breakpoint: 768, settings: { slidesToShow: 5 } },
      { breakpoint: 480, settings: { slidesToShow: 4 } },
    ],
  };

  // If nothing matches, render nothing (since you don't want fallback)
  if (!categoryItems.length) return null;

  return (
    <div className={css.categoryContainer}>
      <div className={css.sectionHead}>
        <h2>{title}</h2>
      </div>

      <div className={css.sectionContent}>
        <div className={css.categorySlider}>
          <Slider {...SliderSettings}>
            {categoryItems.map(({ label, slug, image }, index) => (
              <div
                key={`${slug}-${index}`}
                className={css.categoryItem}
                onClick={() => useHistoryPush('SearchPage', {}, { [paramName]: slug })}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    useHistoryPush('SearchPage', {}, { [paramName]: slug });
                  }
                }}
              >
                <div className={css.categoryImg}>
                  <img src={image} alt={label} />
                </div>
                <h5>{label}</h5>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
}

export default SectionShopByCategory;
